import type {
  OnboardingAssigneeKind,
  OnboardingCaseKind,
  OnboardingCaseStatus,
  OnboardingDueAnchor,
  OnboardingTaskKind,
  OnboardingTaskStatus,
  OnboardingTemplateVersionStatus,
} from '@gb-ticket/shared-types';

export const ONBOARDING_CASE_KIND_LABEL: Record<OnboardingCaseKind, string> = {
  onboarding: 'Eintritt',
  offboarding: 'Austritt',
};

export const ONBOARDING_CASE_STATUS_LABEL: Record<OnboardingCaseStatus, string> = {
  draft: 'Entwurf',
  active: 'Aktiv',
  completed: 'Abgeschlossen',
  cancelled: 'Abgebrochen',
};

export const ONBOARDING_CASE_STAGE_LABEL = {
  preboarding: 'Vorbereitung',
  onboarding: 'Eintritt',
  offboarding: 'Austritt',
} as const;

export type OnboardingCaseStage = keyof typeof ONBOARDING_CASE_STAGE_LABEL;

export function onboardingCaseStage(item: {
  kind: OnboardingCaseKind;
  subjectUserId: string | null;
}): OnboardingCaseStage {
  if (item.kind === 'offboarding') {
    return 'offboarding';
  }
  if (!item.subjectUserId) {
    return 'preboarding';
  }
  return 'onboarding';
}

/** Kind, status, and stage. The stage is omitted when its label repeats the kind. */
export function onboardingCaseStateLabel(item: {
  kind: OnboardingCaseKind;
  status: OnboardingCaseStatus;
  subjectUserId: string | null;
}): string {
  const kind = ONBOARDING_CASE_KIND_LABEL[item.kind];
  const stage = ONBOARDING_CASE_STAGE_LABEL[onboardingCaseStage(item)];
  return [kind, ONBOARDING_CASE_STATUS_LABEL[item.status], stage === kind ? null : stage]
    .filter((part) => part != null)
    .join(' · ');
}

export function onboardingNamedOrDash(value: string | null | undefined): string {
  const named = value?.trim();
  if (named) {
    return named;
  }
  return '—';
}

export function onboardingCaseSubjectName(item: {
  subjectDisplayName?: string;
  subjectFirstName: string | null;
  subjectLastName: string | null;
}): string {
  const named = item.subjectDisplayName?.trim();
  if (named) {
    return named;
  }
  return onboardingNamedOrDash(
    [item.subjectFirstName, item.subjectLastName].filter(Boolean).join(' '),
  );
}

export const ONBOARDING_TASK_STATUS_LABEL: Record<OnboardingTaskStatus, string> = {
  open: 'Offen',
  completed: 'Erledigt',
  skipped: 'Übersprungen',
  not_applicable: 'Nicht zutreffend',
  cancelled: 'Storniert',
};

export const ONBOARDING_CASE_EVENT_LABEL: Record<string, string> = {
  case_started: 'Vorgang gestartet',
  case_completed: 'Vorgang abgeschlossen',
  case_cancelled: 'Vorgang abgebrochen',
  case_reopened: 'Vorgang wieder geöffnet',
  case_dates_changed: 'Daten geändert',
  task_claimed: 'Aufgabe übernommen',
  task_unclaimed: 'Übernahme abgelegt',
  task_completed: 'Aufgabe abgeschlossen',
  task_skipped: 'Aufgabe übersprungen',
  task_cancelled: 'Aufgabe storniert',
  phase_tasks_cancelled: 'Offene Aufgaben einer Phase storniert',
  task_not_applicable: 'Aufgabe nicht zutreffend',
  task_reassigned: 'Aufgabe neu zugewiesen',
  task_system_action: 'Systemaktion ausgeführt',
};

export const ONBOARDING_TASK_KIND_LABEL: Record<OnboardingTaskKind, string> = {
  confirm: 'Bestätigung',
  info: 'Hinweis',
  data: 'Dateneingabe',
  document_provide: 'Dokument bereitstellen',
  document_upload: 'Dokument hochladen',
  review: 'Prüfung',
  asset_issue: 'Asset ausgeben',
  asset_return: 'Asset zurücknehmen',
  vehicle_assign: 'Fahrzeug zuordnen',
  vehicle_return: 'Fahrzeug zurücknehmen',
  external_confirm: 'Externe Bestätigung',
};

export const ONBOARDING_ASSIGNEE_KIND_LABEL: Record<OnboardingAssigneeKind, string> = {
  user: 'Person',
  role: 'Gruppe',
  department: 'Abteilung',
  process_owner: 'Prozessverantwortlich',
  subject: 'Betroffene Person',
  subject_manager: 'Führungskraft',
  team: 'Team',
};

export const ONBOARDING_DUE_ANCHOR_LABEL: Record<OnboardingDueAnchor, string> = {
  entry: 'Eintritt',
  exit: 'Austritt',
  last_working_day: 'Letzter Arbeitstag',
  started: 'Start des Vorgangs',
};

export const ONBOARDING_TEMPLATE_VERSION_STATUS_LABEL: Record<
  OnboardingTemplateVersionStatus,
  string
> = {
  draft: 'Entwurf',
  published: 'Veröffentlicht',
  retired: 'Zurückgezogen',
};
